import { InvariantViolation } from './errors';
import type { DomainEventEmitter } from './events';
import type { StationId } from './types';

export interface AuthCacheEntrySnapshot {
  readonly idTag: string;
  readonly acceptedAt: number;
  readonly expiresAt: number;
  readonly parentIdTag?: string;
}

export class AuthCacheEntry {
  readonly idTag: string;
  readonly acceptedAt: number;
  readonly expiresAt: number;
  readonly parentIdTag: string | null;

  constructor(snap: AuthCacheEntrySnapshot, maxTtlMs: number) {
    if (snap.expiresAt <= snap.acceptedAt) {
      throw new InvariantViolation(
        'AuthCacheEntry',
        'expiresAt > acceptedAt',
        `idTag=${snap.idTag}`,
      );
    }
    if (snap.expiresAt - snap.acceptedAt > maxTtlMs) {
      throw new InvariantViolation(
        'AuthCacheEntry',
        'expiresAt - acceptedAt <= AuthCacheTTL',
        `idTag=${snap.idTag}, ttlMs=${snap.expiresAt - snap.acceptedAt}, max=${maxTtlMs}`,
      );
    }
    this.idTag = snap.idTag;
    this.acceptedAt = snap.acceptedAt;
    this.expiresAt = snap.expiresAt;
    this.parentIdTag = snap.parentIdTag ?? null;
  }

  isExpired(now: number): boolean {
    return now >= this.expiresAt;
  }
}

export interface AuthCacheOptions {
  readonly maxTtlMs: number;
  readonly now?: () => number;
}

export class AuthCache {
  readonly #stationId: StationId;
  readonly #emitter: DomainEventEmitter;
  readonly #entries = new Map<string, AuthCacheEntry>();
  readonly #maxTtlMs: number;
  readonly #now: () => number;

  constructor(stationId: StationId, emitter: DomainEventEmitter, options: AuthCacheOptions) {
    this.#stationId = stationId;
    this.#emitter = emitter;
    this.#maxTtlMs = options.maxTtlMs;
    this.#now = options.now ?? (() => Date.now());
  }

  add(snap: AuthCacheEntrySnapshot): AuthCacheEntry {
    const entry = new AuthCacheEntry(snap, this.#maxTtlMs);
    this.#entries.set(entry.idTag, entry);
    this.#fire();
    return entry;
  }

  remove(idTag: string): boolean {
    const existed = this.#entries.delete(idTag);
    if (existed) this.#fire();
    return existed;
  }

  get(idTag: string): AuthCacheEntry | null {
    const entry = this.#entries.get(idTag);
    if (!entry) return null;
    if (entry.isExpired(this.#now())) {
      this.#entries.delete(idTag);
      this.#fire();
      return null;
    }
    return entry;
  }

  list(): readonly AuthCacheEntry[] {
    return [...this.#entries.values()];
  }

  /** Drops every entry whose expiresAt has passed. Returns the number of removals. */
  expireStale(): number {
    const now = this.#now();
    let removed = 0;
    for (const [idTag, entry] of this.#entries) {
      if (entry.isExpired(now)) {
        this.#entries.delete(idTag);
        removed++;
      }
    }
    if (removed > 0) this.#fire();
    return removed;
  }

  /**
   * Extends the expiry of an entry by `additionalMs`, capped so the total TTL
   * never exceeds `maxTtlMs`. Returns the new entry (if any change happened).
   */
  refreshOnUse(idTag: string, additionalMs: number): AuthCacheEntry | null {
    const entry = this.get(idTag);
    if (!entry) return null;
    const capped = Math.min(entry.expiresAt + additionalMs, entry.acceptedAt + this.#maxTtlMs);
    if (capped === entry.expiresAt) return entry;
    const refreshed = new AuthCacheEntry(
      {
        idTag: entry.idTag,
        acceptedAt: entry.acceptedAt,
        expiresAt: capped,
        ...(entry.parentIdTag !== null ? { parentIdTag: entry.parentIdTag } : {}),
      },
      this.#maxTtlMs,
    );
    this.#entries.set(refreshed.idTag, refreshed);
    this.#fire();
    return refreshed;
  }

  clear(): void {
    if (this.#entries.size === 0) return;
    this.#entries.clear();
    this.#fire();
  }

  #fire(): void {
    this.#emitter.emit({
      kind: 'AuthCacheChanged',
      stationId: this.#stationId,
      at: this.#now(),
      entryCount: this.#entries.size,
    });
  }
}
